import type { RazmerRow } from "../components/OSRazmerTable";
import type { RNRazmerLotSuggestion } from "./useRNRazmerLots";
import { parseDecimalInput, formatQtyForDisplay } from "../utils/decimalInput";

export type RNRazmerLotConfirmation =
  | { rows: RazmerRow[] }
  | {
    error: string;
    message?: string;
  };

export const prepareRNRazmerLotConfirmation = (
  row: RazmerRow,
  suggestion?: RNRazmerLotSuggestion,
): RNRazmerLotConfirmation => {
  if (!suggestion) {
    return { error: "previewPending" };
  }

  const measuredQty = (row.measuredQty || "").trim() || row.requiredQty;
  const requestedQty = parseDecimalInput(measuredQty);
  if (requestedQty === null || suggestion.quantity !== requestedQty) {
    return { error: "previewPending" };
  }

  if (suggestion.lotError || suggestion.shortfall > 0) {
    return {
      error: suggestion.lotError?.messageKey || "shortfall",
      message: suggestion.lotError?.message,
    };
  }

  const availableLots = suggestion.availableLots || suggestion.lots || [];
  const firstLot = availableLots[0];
  const hasManualLot = !!(row.serialNumber || row.expiryDate);
  const chosenLot = availableLots.find(
    (lot) => lot.serial === row.serialNumber && (!row.expiryDate || lot.deadline === row.expiryDate),
  );

  if (hasManualLot && !chosenLot) {
    return { error: "invalidLot" };
  }

  const allocations = !hasManualLot && suggestion.lots.length
    ? suggestion.lots
    : [{
      serial: chosenLot?.serial || firstLot?.serial || "",
      deadline: chosenLot?.deadline || firstLot?.deadline || "",
      quantity: requestedQty,
    }];
  const sourceRowKey = row.sourceRowKey || row.osRowId || "";

  return {
    rows: allocations.map((lot, index) => ({
      ...row,
      // Row keys are plaintext; keep them opaque and store lot data in dedicated fields.
      osRowId: index === 0 ? row.osRowId : `${sourceRowKey}:split:${index}`,
      sourceRowKey: sourceRowKey || row.sourceRowKey,
      measuredQty: allocations.length > 1 ? formatQtyForDisplay(lot.quantity) : measuredQty,
      serialNumber: lot.serial,
      expiryDate: lot.deadline,
      confirmed: true,
      isPreviewActual: false,
      workedByName: "",
      workedAt: "",
      verifiedByName: "",
      verifiedAt: "",
      verifyRequested: false,
    })),
  };
};
